import React from 'react';

import { Box, Grid } from '@mui/material';

import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { useSendInvitesToVerifiersAdminsByCvAdminMutation, VerifiersListDocument } from 'src/api';
import { Button } from 'src/components/common';
import { Field, Form, TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';

import { InviteFormFields, InviteFormValues } from './types';
import { inviteFormValidationSchema } from './utils';

interface InviteVerifiersAdminsByCVAdminModalFormProps {
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const InviteVerifiersAdminsByCVAdminModalForm: React.FC<
  InviteVerifiersAdminsByCVAdminModalFormProps
> = ({ closeModal, switchClosability }) => {
  const { createApolloContext } = useNotification();

  const [sendInvites] = useSendInvitesToVerifiersAdminsByCvAdminMutation({
    refetchQueries: [VerifiersListDocument],
    context: createApolloContext({ successMessage: 'Invitations sent' }),
  });

  const onSubmit = React.useCallback(
    async (formValues: InviteFormValues) => {
      switchClosability(false);

      try {
        await sendInvites({
          variables: {
            verifierAdmin: {
              email: formValues.email,
              firstName: formValues.firstName,
              lastName: formValues.lastName,
              displayName: formValues.displayName,
            },
          },
        });

        switchClosability(true);
        closeModal();
      } catch {
        switchClosability(true);
      }
    },
    [closeModal, sendInvites, switchClosability],
  );

  const onModalCloseClick = React.useCallback(() => closeModal(), [closeModal]);

  return (
    <Box>
      <Form<InviteFormValues>
        onSubmit={onSubmit}
        subscription={{ valid: true, values: true, submitting: true }}
        validate={validateWithYupSchema(inviteFormValidationSchema)}
      >
        {({ submitting, valid }) => {
          return (
            <Grid container spacing={4}>
              <Grid size={12}>
                <Field
                  name={InviteFormFields.displayName}
                  component={TextField}
                  label="Verifier Display Name"
                  fullWidth
                  placeholder="Type Display Name"
                  required
                />
              </Grid>
              <Grid size={12}>
                <Grid container spacing={3}>
                  <Grid size={6}>
                    <Field
                      name={InviteFormFields.firstName}
                      component={TextField}
                      label="Verifier Admin First Name"
                      placeholder="Type First Name"
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid size={6}>
                    <Field
                      name={InviteFormFields.lastName}
                      component={TextField}
                      label="Verifier Admin Last Name"
                      placeholder="Type Last Name"
                      fullWidth
                      required
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid size={12}>
                <Field
                  name={InviteFormFields.email}
                  component={TextField}
                  label="Verifier Admin Email"
                  fullWidth
                  placeholder="Type Email Address"
                  required
                />
              </Grid>
              <Grid size={12}>
                <Grid container spacing={3} justifyContent="center">
                  <Grid>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={onModalCloseClick}
                      disabled={submitting}
                    >
                      Close
                    </Button>
                  </Grid>

                  <Grid>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={!valid || submitting}
                      loading={submitting}
                    >
                      Send invite
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          );
        }}
      </Form>
    </Box>
  );
};
